import { useEffect, useState } from 'react';

import AdminLayout from '../../components/admin/AdminLayout';
import { ResponsiveTable } from '../../components/admin/responsive';
import { api } from '../../services/api';

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
function fmtReais(cents) {
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function Badge({ children, bg, color, border }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: '0.08em',
        padding: '3px 10px',
        borderRadius: 99,
        background: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      {children}
    </span>
  );
}

function StatusSub({ status }) {
  const map = {
    ACTIVE: {
      label: 'Ativa',
      bg: '#dcfce7',
      color: '#166534',
      border: '#bbf7d0',
    },
    OVERDUE: {
      label: 'Inadimplente',
      bg: '#fef9c3',
      color: '#854d0e',
      border: '#fde68a',
    },
    CANCELLED: {
      label: 'Cancelada',
      bg: '#fee2e2',
      color: '#991b1b',
      border: '#fecaca',
    },
    EXPIRED: {
      label: 'Expirada',
      bg: '#f1f5f9',
      color: '#64748b',
      border: '#e2e8f0',
    },
  };
  const s = map[status] ?? {
    label: status,
    bg: '#f1f5f9',
    color: '#64748b',
    border: '#e2e8f0',
  };
  return (
    <Badge bg={s.bg} color={s.color} border={s.border}>
      {s.label}
    </Badge>
  );
}

function PagoBadge({ pago }) {
  return pago ? (
    <Badge bg="#dcfce7" color="#166534" border="#bbf7d0">
      ✓ Pago
    </Badge>
  ) : (
    <Badge bg="#fee2e2" color="#991b1b" border="#fecaca">
      ✗ Não pago
    </Badge>
  );
}

export default function LilyMembers() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState('mensalistas');
  const [busca, setBusca] = useState('');

  // Modal renovação PIX
  const [modalPix, setModalPix] = useState(null); // membro selecionado
  const [eventos, setEventos] = useState([]);
  const [pixForm, setPixForm] = useState({ eventId: '', eventDayId: '' });
  const [pixResult, setPixResult] = useState(null);
  const [renovando, setRenovando] = useState(false);
  const [erroRenovacao, setErroRenovacao] = useState('');

  useEffect(() => {
    api
      .get('/admin/membros')
      .then(({ data }) => setDados(data))
      .catch(console.error)
      .finally(() => setCarregando(false));
    api
      .get('/admin/eventos')
      .then(({ data }) =>
        setEventos(data.filter((e) => e.status === 'PUBLISHED')),
      )
      .catch(console.error);
  }, []);

  async function abrirModalPix(membro) {
    setModalPix(membro);
    setPixForm({ eventId: '', eventDayId: '' });
    setPixResult(null);
    setErroRenovacao('');
  }

  async function gerarPixRenovacao(e) {
    e.preventDefault();
    setRenovando(true);
    setErroRenovacao('');
    try {
      const { data } = await api.post('/admin/membros/pix/renovar', {
        cpf: modalPix.cpf,
        nome: modalPix.name,
        email: modalPix.email,
        whatsapp: modalPix.whatsapp || '',
        eventId: pixForm.eventId,
        eventDayId: pixForm.eventDayId,
      });
      setPixResult(data);
    } catch (err) {
      setErroRenovacao(err.response?.data?.error || 'Erro ao gerar PIX.');
    } finally {
      setRenovando(false);
    }
  }

  const eventoSelecionado = eventos.find((e) => e.id === pixForm.eventId);

  const mensalistas = (dados?.mensalistasDoMes ?? []).filter(
    (m) =>
      m.name?.toLowerCase().includes(busca.toLowerCase()) ||
      m.email?.toLowerCase().includes(busca.toLowerCase()),
  );

  const assinaturas = (dados?.assinaturas ?? []).filter(
    (a) =>
      a.usuario?.name?.toLowerCase().includes(busca.toLowerCase()) ||
      a.usuario?.email?.toLowerCase().includes(busca.toLowerCase()),
  );

  if (carregando)
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 64 }}>
          Carregando...
        </div>
      </AdminLayout>
    );

  return (
    <AdminLayout>
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>Lily Members</h1>
          <p style={S.subtitle}>Membros mensalistas e assinantes do clube</p>
        </div>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nome ou email..."
          style={S.busca}
        />
      </div>

      {/* Cards de resumo */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Mensalistas este mês</div>
          <div style={S.metricValue}>
            {dados?.mensalistasDoMes?.length ?? 0}
          </div>
        </div>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Confirmadas</div>
          <div style={{ ...S.metricValue, color: '#166534' }}>
            {dados?.mensalistasDoMes?.filter((m) => m.status === 'CONFIRMED')
              .length ?? 0}
          </div>
        </div>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Pendentes</div>
          <div style={{ ...S.metricValue, color: '#854d0e' }}>
            {dados?.mensalistasDoMes?.filter((m) => m.status === 'PENDING')
              .length ?? 0}
          </div>
        </div>
        <div style={S.metricCard}>
          <div style={S.metricLabel}>Assinaturas ativas</div>
          <div style={{ ...S.metricValue, color: '#7c3aed' }}>
            {dados?.assinaturas?.filter((a) => a.status === 'ACTIVE').length ??
              0}
          </div>
        </div>
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {[
          {
            key: 'mensalistas',
            label: `Mensalistas do mês (${dados?.mensalistasDoMes?.length ?? 0})`,
          },
          {
            key: 'assinaturas',
            label: `Assinaturas (${dados?.assinaturas?.length ?? 0})`,
          },
          {
            key: 'pix',
            label: `Renovação PIX (${dados?.mensalistasDoMes?.filter((m) => m.payments?.[0]?.billingType === 'PIX' && m.status === 'CONFIRMED').length ?? 0})`,
          },
        ].map((a) => (
          <button
            key={a.key}
            onClick={() => setAba(a.key)}
            style={{
              padding: '8px 18px',
              borderRadius: 99,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              border: 'none',
              background: aba === a.key ? '#ff2d6c' : '#f1f5f9',
              color: aba === a.key ? '#fff' : '#64748b',
            }}
          >
            {a.label}
          </button>
        ))}
      </div>

      {/* Tabela Mensalistas */}
      {aba === 'mensalistas' && (
        <div style={S.card}>
          {mensalistas.length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>
              Nenhuma mensalista encontrada este mês.
            </p>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  {[
                    'Nome',
                    'Email',
                    'CPF',
                    'Horário',
                    'Valor',
                    'Pagamento',
                    'Status do mês',
                  ].map((h) => (
                    <th key={h} style={S.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mensalistas.map((m) => (
                  <tr key={m.id}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>
                        {m.name}
                      </div>
                    </td>
                    <td style={S.td}>
                      <div style={{ fontSize: 12 }}>{m.email}</div>
                    </td>
                    <td
                      style={{ ...S.td, fontFamily: 'monospace', fontSize: 12 }}
                    >
                      {m.cpf}
                    </td>
                    <td style={S.td}>
                      {m.eventDay
                        ? new Date(m.eventDay.startDate).toLocaleString(
                            'pt-BR',
                            {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: 'UTC',
                            },
                          )
                        : '—'}
                    </td>
                    <td style={{ ...S.td, fontWeight: 700 }}>
                      {fmtReais(m.amountInCents)}
                    </td>
                    <td style={S.td}>
                      {m.payments?.[0] ? (
                        <Badge
                          bg={
                            m.payments[0].billingType === 'PIX'
                              ? '#fef9c3'
                              : '#ede9fe'
                          }
                          color={
                            m.payments[0].billingType === 'PIX'
                              ? '#854d0e'
                              : '#5b21b6'
                          }
                          border={
                            m.payments[0].billingType === 'PIX'
                              ? '#fde68a'
                              : '#c4b5fd'
                          }
                        >
                          {m.payments[0].billingType === 'PIX'
                            ? 'PIX'
                            : 'Cartão'}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={S.td}>
                      <PagoBadge pago={m.status === 'CONFIRMED'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Aba PIX Renovação */}
      {aba === 'pix' &&
        (() => {
          const pixMembros = (dados?.mensalistasDoMes ?? []).filter(
            (m) =>
              m.payments?.[0]?.billingType === 'PIX' &&
              m.status === 'CONFIRMED',
          );
          return (
            <div style={S.card}>
              {pixMembros.length === 0 ? (
                <p
                  style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}
                >
                  Nenhuma mensalista PIX confirmada este mês.
                </p>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      {['Nome', 'Email', 'CPF', 'Pago em', 'Ação'].map((h) => (
                        <th key={h} style={S.th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pixMembros.map((m) => (
                      <tr key={m.id}>
                        <td style={S.td}>
                          <div style={{ fontWeight: 700, color: '#0f172a' }}>
                            {m.name}
                          </div>
                        </td>
                        <td style={S.td}>
                          <div style={{ fontSize: 12 }}>{m.email}</div>
                        </td>
                        <td
                          style={{
                            ...S.td,
                            fontFamily: 'monospace',
                            fontSize: 12,
                          }}
                        >
                          {m.cpf}
                        </td>
                        <td style={S.td}>{fmt(m.confirmedAt)}</td>
                        <td style={S.td}>
                          <button
                            onClick={() => abrirModalPix(m)}
                            style={{
                              padding: '7px 14px',
                              background: '#ff2d6c',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 99,
                              fontSize: 11,
                              fontWeight: 900,
                              cursor: 'pointer',
                            }}
                          >
                            Gerar PIX próximo mês
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })()}

      {/* Tabela Assinaturas */}
      {aba === 'assinaturas' && (
        <div style={S.card}>
          {assinaturas.length === 0 ? (
            <p style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>
              Nenhuma assinatura cadastrada.
            </p>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  {[
                    'Membro',
                    'Plano',
                    'Ciclo',
                    'Válida até',
                    'Último pagamento',
                    'Status',
                  ].map((h) => (
                    <th key={h} style={S.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {assinaturas.map((a) => (
                  <tr key={a.id}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>
                        {a.usuario?.name ?? '—'}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {a.usuario?.email}
                      </div>
                    </td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>
                        {a.plano?.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {fmtReais(a.plano?.amountInCents ?? 0)}
                      </div>
                    </td>
                    <td style={S.td}>
                      <Badge bg="#f5f3ff" color="#7c3aed" border="#ddd6fe">
                        {a.plano?.billingCycle}
                      </Badge>
                    </td>
                    <td style={{ ...S.td, fontSize: 13 }}>
                      {fmt(a.validUntil)}
                    </td>
                    <td style={S.td}>
                      {a.ultimoPagamento ? (
                        <div style={{ fontSize: 12 }}>
                          {fmt(a.ultimoPagamento.createdAt)}
                          <br />
                          <span style={{ color: '#94a3b8', fontSize: 11 }}>
                            {a.ultimoPagamento.status}
                          </span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={S.td}>
                      <StatusSub status={a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {/* Modal Renovação PIX */}
      {modalPix && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 20,
              padding: 28,
              width: '100%',
              maxWidth: 540,
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,.15)',
            }}
          >
            <h2
              style={{
                fontSize: 17,
                fontWeight: 900,
                color: '#0f172a',
                marginBottom: 4,
              }}
            >
              Gerar PIX — Renovação
            </h2>
            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 20 }}>
              {modalPix.name} · {modalPix.email}
            </p>

            {!pixResult ? (
              <form
                onSubmit={gerarPixRenovacao}
                style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    fontSize: 10,
                    fontWeight: 900,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  Evento (próximo mês)
                  <select
                    required
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                      fontSize: 16,
                      color: '#0f172a',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      minHeight: '44px',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                    onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                    value={pixForm.eventId}
                    onChange={(e) =>
                      setPixForm({ eventId: e.target.value, eventDayId: '' })
                    }
                  >
                    <option value="">Selecione...</option>
                    {eventos.map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.title}
                      </option>
                    ))}
                  </select>
                </label>
                {eventoSelecionado && (
                  <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      fontSize: 10,
                      fontWeight: 900,
                      color: '#94a3b8',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    Sessão
                    <select
                      required
                      style={{
                        padding: '12px 16px',
                        border: '1px solid #e2e8f0',
                        borderRadius: 10,
                        fontSize: 16,
                        color: '#0f172a',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        minHeight: '44px',
                        boxSizing: 'border-box',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#ff2d6c')}
                      onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                      value={pixForm.eventDayId}
                      onChange={(e) =>
                        setPixForm((f) => ({
                          ...f,
                          eventDayId: e.target.value,
                        }))
                      }
                    >
                      <option value="">Selecione...</option>
                      {eventoSelecionado.dias.map((d) => (
                        <option key={d.id} value={d.id}>
                          {new Date(d.startDate).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'UTC',
                          })}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {erroRenovacao && (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#dc2626',
                      background: '#fee2e2',
                      border: '1px solid #fecaca',
                      borderRadius: 8,
                      padding: 12,
                    }}
                  >
                    {erroRenovacao}
                  </div>
                )}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      window.innerWidth < 480 ? '1fr' : '1fr 1fr',
                    gap: 12,
                    marginTop: 16,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setModalPix(null)}
                    style={{
                      padding: '12px 24px',
                      background: '#f8fafc',
                      color: '#475569',
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      minHeight: '44px',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.background = '#f1f5f9')
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.background = '#f8fafc')
                    }
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={renovando}
                    style={{
                      padding: '12px 24px',
                      background: '#ff2d6c',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 12,
                      fontWeight: 900,
                      cursor: renovando ? 'not-allowed' : 'pointer',
                      opacity: renovando ? 0.5 : 1,
                      minHeight: '44px',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) =>
                      !renovando && (e.target.style.background = '#e02059')
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.background = '#ff2d6c')
                    }
                  >
                    {renovando ? 'Gerando...' : 'Gerar PIX'}
                  </button>
                </div>
              </form>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <p
                  style={{
                    fontSize: 13,
                    color: '#166534',
                    fontWeight: 700,
                    marginBottom: 16,
                  }}
                >
                  ✓ PIX gerado com sucesso!
                </p>
                {pixResult.pixQrCode && (
                  <img
                    src={
                      pixResult.pixQrCode.startsWith('data:')
                        ? pixResult.pixQrCode
                        : `data:image/png;base64,${pixResult.pixQrCode}`
                    }
                    alt="QR Code PIX"
                    style={{
                      width: 180,
                      height: 180,
                      margin: '0 auto 16px',
                      display: 'block',
                      borderRadius: 12,
                      border: '1px solid #f1f5f9',
                    }}
                  />
                )}
                {pixResult.pixCopiaECola && (
                  <div
                    style={{
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: 10,
                      padding: '10px 14px',
                      fontSize: 11,
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      marginBottom: 16,
                      textAlign: 'left',
                    }}
                  >
                    {pixResult.pixCopiaECola}
                  </div>
                )}
                <p style={{ fontSize: 11, color: '#94a3b8', marginBottom: 16 }}>
                  Copie o código acima e envie para {modalPix.email}
                </p>
                <button
                  onClick={() => setModalPix(null)}
                  style={{
                    padding: '12px 24px',
                    background: '#ff2d6c',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: 'pointer',
                    minHeight: '44px',
                    width: '100%',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.target.style.background = '#e02059')}
                  onMouseLeave={(e) => (e.target.style.background = '#ff2d6c')}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const S = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    flexWrap: 'wrap',
    gap: 12,
  },
  h1: { fontSize: 22, fontWeight: 900, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: 13, color: '#94a3b8', margin: '4px 0 0' },
  busca: {
    padding: '10px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 13,
    outline: 'none',
    width: 260,
  },
  metricCard: {
    background: '#fff',
    borderRadius: 12,
    padding: '14px 20px',
    border: '1px solid #f1f5f9',
    flex: 1,
    minWidth: 100,
  },
  metricLabel: {
    fontSize: 11,
    color: '#94a3b8',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 4,
  },
  metricValue: { fontSize: 24, fontWeight: 900, color: '#0f172a' },
  card: {
    background: '#fff',
    borderRadius: 16,
    border: '1px solid #f1f5f9',
    overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    color: '#94a3b8',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    borderBottom: '1px solid #f1f5f9',
    background: '#fafafa',
  },
  td: {
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f8fafc',
  },
};
